import { type ReactElement, type ReactNode } from "react";
import { Bell, House, Search } from "lucide-react";
import { Tooltip } from "../molecules/index.js";
import { STATE_RING } from "../lib/state-ring.js";
import { ChromeIconButton } from "./ChromeIconButton.js";
import { ProfileMenu, type TopBarUser } from "./ProfileMenu.js";

export type { TopBarUser } from "./ProfileMenu.js";

/**
 * Glyph size for the top bar's utility buttons (`ChromeIconButton`
 * `variant="bar"`): 20px, what Fluent, Material and Atlassian put
 * inside a 32px control. The artboard's 16px marks read as lost in the 48px
 * bar (PO live review, d13).
 *
 * The WEIGHT is lighter than Lucide's default, and that is the change rather
 * than the size. At 20px Lucide's stroke of 2 scales to about 1.7px, and three
 * glyphs that heavy in a row read as chunky against the rest of the bar — the
 * New-tab control beside them is a 16px mark, and the tab's own kebab and close
 * are 14px. Thinning to 1.5 (about 1.25px here) settles them without touching
 * the size d13 decided.
 *
 * Internal to this package (TopBar, NotificationsPopover). A host's
 * `ChromeIconButton` glyph is sized by the caller the same way.
 */
export const topBarIconClass = "h-5 w-5 stroke-[1.5]";

export interface TopBarProps {
  /** Click the Home control — host app routes to `/`. */
  onHome?: () => void;
  /** Visual active state for the Home control when the route is `/`. */
  homeActive?: boolean;
  /**
   * Organisation name for the top-left home control. Supplied, the home control
   * becomes a compact mark + full-name lockup; omitted, it stays the plain Home
   * icon, which is also the honest fallback when a session carries no tenant
   * name.
   *
   * A value rather than a slot: this is one string the host reads off the
   * session, not an app-decided sub-tree.
   */
  brandLabel?: string;
  /**
   * Compact tenant mark displayed beside `brandLabel`. Hosts may supply the
   * configured short name; the component keeps the mark legible by showing at
   * most two Unicode code points. Falls back to the first character of the
   * organisation label.
   */
  brandMark?: string;
  /** Click the search icon — host app opens the universal-search dialog. */
  onCmdK?: () => void;
  onPlatformHelp?: () => void;
  /**
   * Replay the onboarding tour for the current page. Capability by
   * prop-presence: the host passes it only where a tour exists, so the menu
   * item never offers to replay nothing.
   */
  onReplayTour?: () => void;
  /**
   * Open the Admin console — host app routes to `/admin`. Capability by
   * prop-presence: the host passes this only for users who may administer
   * the tenant, so the menu item never renders for anyone else.
   */
  onAdmin?: () => void;
  /** Open personal Settings for every role, including tenant administrators. */
  onSettings?: () => void;
  onTeams?: () => void;
  onLogout?: () => void;
  /** Signed-in user — drives the avatar initials and profile header. */
  user?: TopBarUser;
  /**
   * Slot replacing the default bell button. Host app wires a Notifications popover
   * (consuming `@playbookai/ui`'s `Popover`) so the lib stays decoupled from
   * notification data.
   */
  notificationsSlot?: ReactNode;
  /**
   * Slot rendered after Notifications — hosts the Knowledge Studio entry
   * (ADR-0014 retained the historical name when the Player AI trigger left the
   * bar). The library only provides placement; the data binding lives in the
   * host app.
   */
  playerAiSlot?: ReactNode;
  /**
   * Slot between the Knowledge Studio entry and the avatar for the host's
   * theme control (decision d9): an action button named for the theme it
   * switches to, or a same-size spacer while the preference is still loading.
   * Placement only — the preference, its status and the write stay in the host.
   */
  themeToggleSlot?: ReactNode;
  /**
   * Slot rendered between Home and the tab strip. The shell uses this for a
   * global project-context selector pill so project state stays visible.
   */
  projectContextSlot?: ReactNode;
  /**
   * Slot for the tab strip between the Home control and the right-hand action
   * group. The host app passes a wired-up `TabStrip` from `@playbookai/ui`;
   * the slot's bottom edge is the strip's baseline.
   */
  tabsSlot?: ReactNode;
}

/**
 * Persistent application chrome — Home lockup, optional project context,
 * tab strip slot, then Search / notifications / Knowledge Studio / theme
 * slots and the profile popover (PB-5176, Direction A).
 *
 * Data-blind by design: every interactive surface is either a callback prop or
 * a `ReactNode` slot. The host app wires popovers from `@playbookai/ui`'s
 * primitives directly. See ADR-0009.
 */
export function TopBar({
  onHome,
  homeActive = false,
  onCmdK,
  brandLabel,
  brandMark,
  onPlatformHelp,
  onReplayTour,
  onAdmin,
  onSettings,
  onTeams,
  onLogout,
  user,
  notificationsSlot,
  playerAiSlot,
  themeToggleSlot,
  projectContextSlot,
  tabsSlot,
}: TopBarProps): ReactElement {
  return (
    <header className="sticky top-0 z-50 flex h-12 items-center border-b border-pb-border bg-pb-shell text-sm">
      {brandLabel ? (
        /* Branded home control: the organisation reads as the product mark in
           the top-left corner and takes you home, which is where enterprise
           users already look for both. The whole cell is the control, as the
           artboard draws it, with the elevated fill while Home is the route.

           The accessible name keeps the visible organisation label intact and
           adds the destination, satisfying WCAG 2.5.3 (Label in Name). */
        <Tooltip content="Home" describeChild={false} wrapperClassName="h-full">
          <button
            type="button"
            onClick={onHome}
            aria-label={`${brandLabel} Home`}
            aria-current={homeActive ? "page" : undefined}
            className={`flex h-full max-w-56 items-center gap-2 border-e border-pb-border px-3 transition-colors hover:pb-overlay-paint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pb-teal ${
              // The grey fill barely shows in high contrast; the ring does (d16).
              homeActive ? `bg-pb-card-elevated ${STATE_RING.teal}` : ""
            }`}
          >
            <span
              aria-hidden="true"
              className="grid h-5 w-5 shrink-0 place-items-center rounded bg-pb-teal text-dense-1 font-bold uppercase text-pb-on-accent"
            >
              {brandMark?.trim()
                ? Array.from(brandMark.trim()).slice(0, 2).join("")
                : brandLabel.trim().charAt(0)}
            </span>
            <span className="truncate text-sm font-semibold tracking-[-0.01em] text-pb-text">
              {brandLabel}
            </span>
          </button>
        </Tooltip>
      ) : (
        <div className="flex h-full items-center border-e border-pb-border px-3">
          <Tooltip content="Home" describeChild={false}>
            <ChromeIconButton
              variant="bar"
              onClick={onHome}
              aria-label="Home"
              current={homeActive}
            >
              <House className={topBarIconClass} strokeWidth={2} />
            </ChromeIconButton>
          </Tooltip>
        </div>
      )}

      {projectContextSlot ? (
        <div className="flex h-full items-center border-e border-pb-border ps-2 pe-2">
          {projectContextSlot}
        </div>
      ) : null}

      {/* `relative`: the tab strip positions itself over this slot and 1px
          past its bottom, so its tabs sit on the bar's border (d11). */}
      <div className="relative h-full min-w-0 flex-1">{tabsSlot}</div>

      {/* Utility cluster in the artboard's order: Search · notifications ·
          Knowledge Studio · theme · profile — 4px apart (the artboard's 2px)
          so adjacent hover washes read as separate controls (d13). */}
      <div className="flex h-full items-center gap-1 border-s border-pb-border px-2.5">
        {/* The shortcut rides on aria-keyshortcuts (both chords the handler
            accepts), so the tooltip does not describe the button again. */}
        <Tooltip content="Search (⌘K)" describeChild={false}>
          <ChromeIconButton
            variant="bar"
            onClick={onCmdK}
            aria-label="Search"
            aria-keyshortcuts="Meta+K Control+K"
          >
            <Search className={topBarIconClass} />
          </ChromeIconButton>
        </Tooltip>
        {notificationsSlot ?? (
          <ChromeIconButton
            variant="bar"
            aria-label="Notifications"
            className="relative"
          >
            <Bell className={topBarIconClass} />
          </ChromeIconButton>
        )}
        {playerAiSlot}
        {themeToggleSlot}
        <ProfileMenu
          user={user}
          onAdmin={onAdmin}
          onSettings={onSettings}
          onTeams={onTeams}
          onPlatformHelp={onPlatformHelp}
          onReplayTour={onReplayTour}
          onLogout={onLogout}
        />
      </div>
    </header>
  );
}
